import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {BenutzerModels} from 'src/app/models/benutzer-models';
import {SwPush} from '@angular/service-worker';
import {MessageService} from '../services/message.service';
import {SwissAppConfigModels} from '../models/swissappconfig-models';
import {VinavonUpdateService} from '../services/vinavon-update.service';
import {UserAuthenticationService} from '../services/user-authentication.service';
import {HelperService} from '../services/helper.service';
import {environment} from 'src/environments/environment';
import {BehaviorSubject, map, Observable} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SwissSettingsService} from '../services/swiss-settings.service';
import UserLicenses = BenutzerModels.UserLicenses;

@Component({
    selector: 'app-benutzer-anmeldung', templateUrl: './benutzer-anmeldung.component.html', styleUrls: ['./benutzer-anmeldung.component.scss'],
})
export class BenutzerAnmeldungComponent implements OnInit {
    public anwender?: BenutzerModels.Anwender = undefined;
    public mandanten: SwissAppConfigModels.SwissAppMandant[] = [];
    public vinavonVersion: string = environment.version;
    @ViewChild('errorMessageBox') errorMessageBox?: ElementRef<HTMLElement>;
    protected form: UntypedFormGroup;
    protected currBenutzer: BenutzerModels.Benutzer = new BenutzerModels.EmptyUser();
    protected errorMessage$ = new BehaviorSubject('');
    protected hasLoginError$: Observable<boolean> = this.errorMessage$.pipe(
        takeUntilDestroyed(),
        map(value => value.length > 0)
    );
    private readonly publicKey = 'BH10-8b3VWdSL0__S-j06u04qLRuNzqLruAdX-ox1gHs5eZM-iy6tG8lme7dxbRthFs8vhGxynz5a0sA4J2Bc70';

    constructor(private fb: UntypedFormBuilder,
                private route: ActivatedRoute,
                private router: Router,
                private userAuthService: UserAuthenticationService,
                private swPush: SwPush,
                private data: MessageService,
                protected svcVinavonUpdate: VinavonUpdateService,
                protected helperService: HelperService,
                private svcSettings: SwissSettingsService) {
        this.form = this.fb.group({
            username: ['', Validators.required], password: ['', Validators.required],
        });
    }

    ngOnInit(): void {
        this.route.params.subscribe({
            next: async (params) => {
                const value = params['errstate'];
                if (value) {
                    switch (value) {
                        case '401':
                            this.showError('MAIN_APP_LOGIN.UNAUTHORIZED');
                            break;
                        case '403':
                            switch (params['errmsg']) {
                                case 'navigator':
                                    this.showError('MAIN_APP_LOGIN.NAVIGATION_FAILED_NAVIGATOR');
                                    await this.resetStartPage(UserLicenses.CNavigatorMobile);
                                    break;
                                case 'serviceapp':
                                    this.showError('MAIN_APP_LOGIN.NAVIGATION_FAILED_SERVICEAPP');
                                    await this.resetStartPage(UserLicenses.CServiceApp);
                                    break;
                                case 'timeapp':
                                    this.showError('MAIN_APP_LOGIN.NAVIGATION_FAILED_TIMEAPP');
                                    await this.resetStartPage(UserLicenses.CMobileTime);
                                    break;
                                case 'timeappplus':
                                default:
                                    this.showError('MAIN_APP_LOGIN.NAVIGATION_FAILED');
                            }
                            break;
                        case '404':
                            this.showError('MAIN_APP_LOGIN.PAGE_NOT_FOUND');
                            await this.attemptAutoLogin();
                            break;
                        default:
                            this.showError('MAIN_APP_LOGIN.NAVIGATION_FAILED');
                            break;
                    }
                } else {
                    await this.attemptAutoLogin();
                }
            },
        });

        this.pushSubscription().then(notificationPermission => {
            if (notificationPermission) {
                this.swPush.messages.subscribe({next: (message) => console.log(message)});
            }
        });
    }

    async resetStartPage(lic: UserLicenses): Promise<void> {
        if (!(await this.userAuthService.hasLicences(lic))) {
            this.showError('MAIN_APP_LOGIN.STARTPAGE_NAVIGATION_INVALID');
            await this.svcSettings.setStartPage(-1);
        }
    }

    async pushSubscription(): Promise<boolean> {
        if (!this.swPush.isEnabled) {
            console.log('Die Notification ist nicht aktiviert.');
            return false;
        }

        const sub = await this.swPush.requestSubscription({
            serverPublicKey: this.publicKey,
        });
        this.data.addUserToMessageReceiver(sub);
        return true;
    }

    async onSubmit(): Promise<void> {
        if (this.form.valid) {
            this.currBenutzer.userName = this.helperService.encryptUsingAES256(this.form.get('username')?.value);
            this.currBenutzer.userPW = this.helperService.encryptUsingAES256(this.form.get('password')?.value);

            this.userAuthService.loginUser(this.currBenutzer).then(validLoginAttempt => {
                if (validLoginAttempt) {
                    this.checkUserLoginState();
                } else {
                    this.showError('MAIN_APP_LOGIN.WRONG_USERDATA');
                }
            });
        } else {
            this.showError('MAIN_APP_LOGIN.ADD_PW_AND_USERNAME');
        }
    }

    private async attemptAutoLogin(): Promise<void> {
        const reLoginSuccess = await this.userAuthService.tryReLogin();
        if (reLoginSuccess) {
            await this.checkUserLoginState();
        } else {
            console.error('AUTOLOGIN FAILED');
        }
    }

    private async checkUserLoginState(): Promise<boolean> {
        const canLogIn = await this.userAuthService.checkUserLicenses();
        if (canLogIn) {
            // required so the user is set correctly in userAuthService
            const anwender = await this.userAuthService.loadAnwenderData();

            this.userAuthService.startCheckingForOtherLogins();

            const licences = await this.userAuthService.getCurrentUserLicense();
            if (licences.length === 0) {
                this.showError('MAIN_APP_LOGIN.NO_LICENSES_FOUND');
                return false;
            }

            if (licences.length === 1) {
                if (!(await this.navigateByLicense(licences[0]))) {
                    this.showError('MAIN_APP_LOGIN.NAVIGATION_FAILED');
                    return false;
                }
                return true;
            }

            const config = await this.svcSettings.loadUserConfig();
            if (config.startPage > 0) {
                switch (config.startPage) {
                    case 1:
                        if (!(await this.userAuthService.hasLicences(BenutzerModels.UserLicenses.CNavigatorMobile))) {
                            await this.resetStartPage(UserLicenses.CMobileTime);
                            break;
                        }
                        return await this.router.navigate(['dashboard']);
                    case 2:
                        if (!(await this.userAuthService.hasLicences(BenutzerModels.UserLicenses.CNavigatorMobile))) {
                            await this.resetStartPage(UserLicenses.CMobileTime);
                            break;
                        }
                        return await this.router.navigate(['adressen']);
                    case 3:
                        if (!(await this.userAuthService.hasLicences(BenutzerModels.UserLicenses.CNavigatorMobile))) {
                            await this.resetStartPage(UserLicenses.CMobileTime);
                            break;
                        }
                        return await this.router.navigate(['objekte']);
                    case 4:
                        if (!(await this.userAuthService.hasLicences(BenutzerModels.UserLicenses.CNavigatorMobile))) {
                            await this.resetStartPage(UserLicenses.CMobileTime);
                            break;
                        }
                        return await this.router.navigate(['pendenzen']);
                    case 5:
                        if (!(await this.userAuthService.hasLicences(BenutzerModels.UserLicenses.CServiceApp))) {
                            await this.resetStartPage(UserLicenses.CMobileTime);
                            break;
                        }
                        return await this.router.navigate(['serviceapp/saagenda']);
                    case 6:
                        if (!(await this.userAuthService.hasLicences(BenutzerModels.UserLicenses.CMobileTime))) {
                            await this.resetStartPage(UserLicenses.CMobileTime);
                            break;
                        }
                        return await this.router.navigate(['timeapp']);
                }
            }

            await this.navigateByAllLicences(licences);
        }
        return canLogIn;
    }

    private async navigateByAllLicences(licences: BenutzerModels.BenutzerLizenz[]): Promise<boolean> {
        for (const lic of licences) {
            if ((await this.navigateByLicense(lic))) {
                return true;
            }
        }
        return false;
    }

    private async navigateByLicense(licence: BenutzerModels.BenutzerLizenz): Promise<boolean> {
        switch (licence.lizenzName) {
            case BenutzerModels.UserLicenses.CNavigatorMobile:
                return await this.router.navigate(['/dashboard']);
            case BenutzerModels.UserLicenses.CServiceApp:
                return await this.router.navigate(['/serviceapp/saagenda']);
            case BenutzerModels.UserLicenses.CMobileTime:
                return await this.router.navigate(['/timeapp']);
        }
        return false;
    }

    private showError(text: string): void {
        this.errorMessage$.next(text);
        this.errorMessageBox?.nativeElement.classList.add('animate');
        setTimeout(() => {
            this.errorMessageBox?.nativeElement.classList.remove('animate');
        }, 500);
    }

}
